import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';
import { useEffect, useTransition, useRef, useCallback, useMemo, startTransition, useState } from 'react';
import { debounce } from 'lodash';
import { Plus, UserRoundSearch } from 'lucide-react';
import { toast } from 'sonner';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { withSuspense } from 'src/hoc';
import { InputSearch } from '@/components/ui/input-search';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { Button } from '@/components/ui/button';
import { EscrowMemberRoleEnum } from 'src/graphql/schema';
import { EscrowMemberCreateDocument } from 'src/graphql/actions/escrowMembers/escrowMemberCreate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from 'src/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { EmptyBlock } from '@/components/ui/empty-block';

import { refetchEscrowMembers } from '../api';
import { useMembersManagement } from '../model/MembersManagementContext';

import type { IUsersQuery } from 'src/graphql/actions/user/users.query';
import type { IEscrowMembersQuery } from 'src/graphql/actions/escrowMembers/escrowMembers.query';

import AddUserDialog from './AddUserDialog';

type IUserRow = {
  id: string;
  user: {
    name: string;
    email: string;
    isAdded: boolean;
  };
};

export const UserAddedTooltip = () => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost">
          Add <Plus />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="center" className="bg-white text-sm text-popover-foreground shadow-md">
        Already added
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const AddMemberButton = ({ id: userId = '', canAdd = false }) => {
  const { escrowPartyId } = useMembersManagement();
  const [createEscrowMember, { loading }] = useMutation(EscrowMemberCreateDocument);

  const onCreate = async () => {
    try {
      const { data } = await createEscrowMember({
        variables: {
          input: {
            escrowPartyId,
            userId,
            escrowMemberParams: {
              role: EscrowMemberRoleEnum.Viewer,
            },
          },
        },
      });

      if (data?.escrowMemberCreate?.success) {
        toast.success('Member access has been granted');
        void refetchEscrowMembers();
      } else {
        throw new ClientError(data?.escrowMemberCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  if (!canAdd) return <UserAddedTooltip />;

  return (
    <Button variant="ghost" className="shrink-0" onClick={onCreate} disabled={loading} isLoading={loading}>
      Add
      <Plus />
    </Button>
  );
};

const columnHelper = createColumnHelper<IUserRow>();
const userColumn = columnHelper.accessor('user', {
  cell: (props) => {
    const { name, email } = props.getValue();
    const fallback = name?.charAt(0).toUpperCase();

    return (
      <div className="flex items-center gap-2">
        <Avatar className="size-8 md:size-10 rounded-full">
          <AvatarImage src={getGravatarUrl(email)} />
          <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
        </Avatar>
        <span className="text-sm text-foreground font-medium line-clamp-1">{name}</span>
      </div>
    );
  },
});
const addMemberColumn = columnHelper.accessor('id', {
  cell: (props) => (
    <div className="flex justify-end">
      <AddMemberButton id={props.row.original.id} canAdd={!props.row.original.user.isAdded} />
    </div>
  ),
});

const OrganizationMembers = () => {
  const [queryFilter, setQueryFilter] = useState<string | undefined>(undefined);
  const currentPage = useRef(1);
  const isLoadingMore = useRef(false);
  const { queryEscrowMembersRef, canCreateMember } = useMembersManagement();

  const {
    data: { users },
    refetch,
    fetchMore,
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      page: 1,
      perPage: 20,
      ...(queryFilter && { queryFilter }),
    },
  });
  const [isPending, startSearchTransition] = useTransition();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const {
    data: { escrowMembers },
  } = useReadQuery(queryEscrowMembersRef);

  const handleLoadMore = async () => {
    if (isLoadingMore.current || !users.hasNextPage) return;

    isLoadingMore.current = true;
    const nextPage = currentPage.current + 1;

    try {
      await fetchMore({
        variables: {
          page: nextPage,
          perPage: 20,
          ...(queryFilter && { queryFilter }),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.users?.nodes?.length) return prev;

          currentPage.current = nextPage;

          return {
            ...prev,
            users: {
              ...fetchMoreResult.users,
              hasNextPage: fetchMoreResult.users.hasNextPage,
              nodes: [...prev.users.nodes, ...fetchMoreResult.users.nodes],
            },
          };
        },
      });
    } catch (error) {
      toast.error('Failed to load more users');
    } finally {
      isLoadingMore.current = false;
    }
  };

  const onLoadMore = useCallback(handleLoadMore, [fetchMore]);

  const handleClear = () => startSearchTransition(() => setQueryFilter(undefined));

  const onClear = useCallback(handleClear, [refetch]);

  const debouncedOnSearch = useCallback(
    debounce((e) => {
      const searchValue = e.target.value;
      startSearchTransition(() => setQueryFilter(searchValue));
    }, 500),
    [startSearchTransition],
  );

  const filteredUsers = useMemo(
    () => filterUsers(users.nodes, escrowMembers.nodes),
    [users.nodes, escrowMembers.nodes],
  );

  const columns = useMemo(() => (canCreateMember ? [userColumn, addMemberColumn] : [userColumn]), [canCreateMember]);

  const handleScroll = useCallback(
    () =>
      startTransition(() => {
        if (loadMoreRef.current && tableRef.current) {
          const { bottom: loadMoreBottom } = loadMoreRef.current.getBoundingClientRect();
          const { top: tableTop, height: tableHeight } = tableRef.current.getBoundingClientRect();
          const visibleHeight = window.innerHeight - tableTop;

          if (loadMoreBottom <= tableTop + Math.min(visibleHeight, tableHeight) + 100) {
            void onLoadMore();
          }
        }
      }),
    [onLoadMore],
  );

  const checkVisibility = useCallback(() => {
    startTransition(() => {
      if (loadMoreRef.current && tableRef.current) {
        const { bottom: loadMoreBottom } = loadMoreRef.current.getBoundingClientRect();
        const { top: tableTop } = tableRef.current.getBoundingClientRect();
        const visibleHeight = window.innerHeight - tableTop;

        if (loadMoreBottom <= tableTop + visibleHeight + 100) {
          void onLoadMore();
        }
      }
    });
  }, [onLoadMore]);

  useEffect(() => {
    const tableElement = tableRef.current;

    // Initial check when component mounts
    checkVisibility();

    // Add scroll listener
    if (tableElement) {
      tableElement.addEventListener('scroll', handleScroll);
    }

    // Add resize listener
    window.addEventListener('resize', checkVisibility);

    return () => {
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkVisibility);
    };
  }, [handleScroll, checkVisibility]);

  const table = useReactTable({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4 overflow-hidden flex flex-col px-6 max-h-[400px] md:max-h-none">
      <h2 className="text-base text-foreground">
        Add escrow members <i className="font-normal text-xs text-gray-400">Optional</i>
      </h2>
      <InputSearch placeholder="Search" onChange={debouncedOnSearch} onClear={onClear} isLoading={isPending} />
      <div className="rounded-sm border border-border grow-1 overflow-hidden">
        <Table ref={tableRef} maxHeight="h-full">
          <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
            <TableRow>
              <TableHead colSpan={columns.length}>Your organization members ({users.nodesCount})</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.nodesCount ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className={cn(row.original.user.isAdded && 'opacity-50')}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-1.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-6">
                  <EmptyBlock
                    withCard={false}
                    icon={UserRoundSearch}
                    title={queryFilter ? 'No results found' : 'You don’t have members in your organization'}
                    action={<AddUserDialog onSuccess={handleClear} />}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            {users.hasNextPage && !isPending && (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-1.5">
                  <div ref={loadMoreRef} className="h-10" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(OrganizationMembers);

function filterUsers(users: IUsersQuery['users']['nodes'], members: IEscrowMembersQuery['escrowMembers']['nodes']) {
  const usersSet = new Set(members.map(({ user: { id } }) => id));

  return users.map((user) => ({
    id: user.id,
    user: {
      name: user.name || '',
      email: user.email,
      isAdded: usersSet.has(user.id),
    },
  }));
}
